import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNavigationContext } from "../../common/navigation/NavigationContext";

// --- 🎯 GENERIC ENGINE & CONFIG ---
import GenericCtrl from "./GenericCore/SEC_03_GenericManagerController";
import { SEC_03_USERMANAGEMENT_CONFIG } from "./SEC_03_USERMANAGEMENT_CONFIG";

// --- EXISTING HARDCODED FEATURE IMPORTS ---
import SEC_03_UserManagementController from "./Users/SEC_03_UserManagementController";
import SEC_03_RoleManagementController from "./Roles/SEC_03_RoleManagementController";
import SEC_03_CapabilityManagementController from "./Capabilities/SEC_03_CapabilityManagementController"; 

// Alias to PascalCase to satisfy ESLint naming rules
const UserCtrl = SEC_03_UserManagementController;
const RoleCtrl = SEC_03_RoleManagementController;
const CapCtrl = SEC_03_CapabilityManagementController;

export default function SEC_03_UserManagementMainView() {
  const navigate = useNavigate();
  useNavigationContext(); 
  
  // 🎯 Programmatic Sort: Order by the 'ordinal' property
  const genericKeys = Object.keys(SEC_03_USERMANAGEMENT_CONFIG).sort((a, b) => {
    return SEC_03_USERMANAGEMENT_CONFIG[a].ordinal - SEC_03_USERMANAGEMENT_CONFIG[b].ordinal;
  });

  const [activeFeature, setActiveFeature] = useState(genericKeys[0] || "ResourceTypes");

  return (
    <div className="page-main mt-5 pt-5">
      <div className="page-main-inner">
        <section className="page-section px-4">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-xl-11">

                <div className="mb-4">
                  <h2 className="h4 fw-bold mb-1 text-dark">Access Control Management</h2>
                  <p className="text-muted extra-small mb-0 text-uppercase fw-bold">
                    Site Administration & Hierarchy
                  </p>
                </div>

                {/* 🎯 2. SYSTEM USAGE GUIDE: SEAMLESS PILLAR INTEGRATION */}
                <div className="alert alert-light border-0 shadow-sm p-4 mb-4 bg-white">
                  <h6 className="fw-bold small text-uppercase mb-2 text-primary">System Usage Guide</h6>
                  <p className="small text-muted mb-2">
                    This portal manages the <strong>Security Infrastructure</strong>. Use the tabs below to configure technical anchors, organizational boundaries, the capability dictionary, functional permissions, and user identities.
                  </p>
                  <div className="row g-2 mt-1">
                    
                    {/* 🎯 DYNAMIC PILLARS (0, 1, 2) */}
                    {genericKeys.map((key) => (
                      <div className="col" key={key}>
                        <div className="p-2 border-start border-3 border-primary bg-light-subtle h-100">
                          <span className="fw-bold extra-small d-block text-dark text-uppercase">
                            {SEC_03_USERMANAGEMENT_CONFIG[key].ordinal}. {SEC_03_USERMANAGEMENT_CONFIG[key].title}
                          </span>
                          <span className="extra-small text-muted">
                            {SEC_03_USERMANAGEMENT_CONFIG[key].description}
                          </span>
                        </div>
                      </div>
                    ))}

                    {/* 🎯 LEGACY PILLARS (3, 4, 5) */}
                    <div className="col">
                      <div className="p-2 border-start border-3 border-success bg-light-subtle h-100">
                        <span className="fw-bold extra-small d-block text-dark">3. CAPABILITIES</span>
                        <span className="extra-small text-muted">The Verb dictionary.</span>
                      </div>
                    </div>
                    <div className="col">
                      <div className="p-2 border-start border-3 border-warning bg-light-subtle h-100">
                        <span className="fw-bold extra-small d-block text-dark">4. ROLES</span>
                        <span className="extra-small text-muted">Permission sets.</span>
                      </div>
                    </div>
                    <div className="col">
                      <div className="p-2 border-start border-3 border-dark bg-light-subtle h-100">
                        <span className="fw-bold extra-small d-block text-dark">5. USERS</span>
                        <span className="extra-small text-muted">Matrix mapping.</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 🎯 3. UNIFIED TAB BAR */}
                <div className="card shadow-sm border-0 bg-white mb-3">
                  <div className="card-body p-2 d-flex gap-2 text-center">
                    
                    {/* DYNAMIC CONFIG TABS (LHS) */}
                    {genericKeys.map(key => (
                      <button 
                        key={key}
                        className={`btn btn-sm flex-grow-1 fw-bold extra-small py-2 ${activeFeature === key ? 'btn-primary text-white shadow-sm' : 'btn-light text-muted border-0'}`}
                        onClick={() => setActiveFeature(key)}
                      >
                        <i className={`bi ${key === 'ResourceTypes' ? 'bi-cpu' : key === 'Resources' ? 'bi-hdd-network' : 'bi-diagram-3'} me-2`}></i>
                        {SEC_03_USERMANAGEMENT_CONFIG[key].title.toUpperCase()}
                      </button>
                    ))}

                    {/* LEGACY HARDCODED TABS (RHS) */}
                    <button 
                      className={`btn btn-sm flex-grow-1 fw-bold extra-small py-2 ${activeFeature === 'CAPABILITIES' ? 'btn-dark text-white' : 'btn-light text-muted border-0'}`}
                      onClick={() => setActiveFeature("CAPABILITIES")}
                    >
                      <i className="bi bi-key me-2"></i>CAPABILITIES
                    </button>
                    <button 
                      className={`btn btn-sm flex-grow-1 fw-bold extra-small py-2 ${activeFeature === 'ROLES' ? 'btn-dark text-white' : 'btn-light text-muted'}`}
                      onClick={() => setActiveFeature("ROLES")}
                    >
                      <i className="bi bi-shield-lock me-2"></i>ROLES
                    </button>
                    <button 
                      className={`btn btn-sm flex-grow-1 fw-bold extra-small py-2 ${activeFeature === 'USERS' ? 'btn-dark text-white' : 'btn-light text-muted'}`}
                      onClick={() => setActiveFeature("USERS")}
                    >
                      <i className="bi bi-people me-2"></i>USERS
                    </button>
                  </div>
                </div>

                {/* 🎯 4. DYNAMIC CONTENT AREA */}
                <div className="card shadow-sm border-0 bg-white" style={{ minHeight: '400px' }}>
                  <div className="card-body p-0">
                    
                    {/* GENERIC HANDLER */}
                    {genericKeys.includes(activeFeature) && (
                        <GenericCtrl 
                          key={activeFeature} /* 🎯 FIX: Forces re-mount to refresh data when switching generic tabs */
                          config={SEC_03_USERMANAGEMENT_CONFIG[activeFeature]} 
                          allConfigs={SEC_03_USERMANAGEMENT_CONFIG}
                        />
                    )}

                    {/* LEGACY HANDLERS */}
                    {activeFeature === "CAPABILITIES" && <CapCtrl />}
                    {activeFeature === "ROLES" && <RoleCtrl />}
                    {activeFeature === "USERS" && <UserCtrl />}
                  </div>
                </div>

                {/* 5. FOOTER */}
                <div className="mt-5 pt-4 border-top">
                  <button 
                    className="btn btn-link text-decoration-none p-0 text-secondary small d-flex align-items-center shadow-none" 
                    onClick={() => navigate("/registered/site-admin")}
                  >
                    <i className="bi bi-arrow-left me-2"></i>Return to Admin Dashboard
                  </button>
                </div>

              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}